import React from "react"
import { useSelector, useDispatch } from "react-redux"

import { DESC } from "const"
import { sort } from "client/store/utils"
import {
    StoreType,
    ResultDatapointListType,
    CalculateQidResultProps,
    CalculateQidResultData,
    setQidResultExpandedModelIds,
    setQidResultExpandedSafetyFunctionIds,
    setQidResultExpandedOptionIds,
} from "client/store"
import {
    RowSizeType,
    TableRow,
    TableColumn,
    TableCell,
    TableCellContentWrapper,
    TableCellContent,
    NoDatapoints,
} from "client/components"

import Datapoint from "./nestedDatapoints"

import {
    TableRowLevelStyled,
    ModelTitle,
    DatapointRow,
} from "./styled"

const rowFitSize: keyof RowSizeType = "fit"
const rowSmSize: keyof RowSizeType = "sm"
const datapointWrapperStyle: React.CSSProperties = { flexDirection: "column" }

const Rows = (columns: TableColumn[]) => {
    const modelList = useSelector((state: StoreType) => state.qidModel.modelList)
    const datapointList = useSelector((state: StoreType) => state.qidResult.datapointList) as ResultDatapointListType
    const method = useSelector((state: StoreType) => state.qidResult.calculationMethod)

    const modelIds = useSelector((state: StoreType) => state.qidResult.modelIds)
    const activeModel = useSelector((state: StoreType) => state.qidResult.model)
    const activeSafetyFunction = useSelector((state: StoreType) => state.qidResult.safetyFunction)
    const activeOption = useSelector((state: StoreType) => state.qidResult.option)

    const expandedModelIds = useSelector((state: StoreType) => state.qidResult.expandedModelIds)
    const expandedSafetyFunctionIds = useSelector((state: StoreType) => state.qidResult.expandedSafetyFunctionIds)
    const expandedOptionIds = useSelector((state: StoreType) => state.qidResult.expandedOptionIds)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const toggleModel = (modelId: number) => {
        const expandedModels = expandedModelIds.slice()
        if (expandedModels.includes(modelId)) {
            dispatch(setQidResultExpandedModelIds(expandedModels.filter((id) => id !== modelId)))
        } else {
            expandedModels.push(modelId)
            dispatch(setQidResultExpandedModelIds(expandedModels))
        }
    }

    const toggleSafetyFunction = (functionId: number) => {
        const expandedFunctions = expandedSafetyFunctionIds.slice()
        if (expandedFunctions.includes(functionId)) {
            dispatch(setQidResultExpandedSafetyFunctionIds(expandedFunctions.filter((id) => id !== functionId)))
        } else {
            expandedFunctions.push(functionId)
            dispatch(setQidResultExpandedSafetyFunctionIds(expandedFunctions))
        }
    }

    const toggleOption = (optionId: number) => {
        const expandedOptions = expandedOptionIds.slice()
        if (expandedOptions.includes(optionId)) {
            dispatch(setQidResultExpandedOptionIds(expandedOptions.filter((id) => id !== optionId)))
        } else {
            expandedOptions.push(optionId)
            dispatch(setQidResultExpandedOptionIds(expandedOptions))
        }
    }

    const rows: React.ReactNode[] = []

    const baseCalculationProps: CalculateQidResultProps = {
        models: { modelIds, modelList },
        datapointList,
        method,
        modelId: null,
        safetyFunctionId: null,
        optionId: null,
        datapointId: null,
    }

    // top level row, show only when no model selected
    if (!activeModel) {
        const topLevelData = CalculateQidResultData(baseCalculationProps)
        rows.push(
            <TableRow key="top_level" size={rowFitSize}>
                {
                    columns.map((column) => (
                        <TableCell
                            key={column.field}
                            cellWidth={column.cellWidth}
                            cellMinWidth={column.cellMinWidth}
                            cellMaxWidth={column.cellMaxWidth}
                        >
                            <TableCellContentWrapper>
                                <TableCellContent>
                                    {
                                        column.field === "title"
                                            ? <b>{lang.topLevel}</b>
                                            : column.formatter(topLevelData)
                                    }
                                </TableCellContent>
                            </TableCellContentWrapper>
                        </TableCell>
                    ))
                }
            </TableRow>,
            <TableRow key="delimiter" size={rowSmSize}>
                <TableCell colSpan={100} />
            </TableRow>,
        )
    }

    modelIds.forEach((modelId) => {
        const model = modelList[modelId]
        if (!model) return

        if (!activeModel || (activeModel && activeModel.id === model.id)) {
            if (!activeSafetyFunction) {
                const modelAvg = CalculateQidResultData({
                    ...baseCalculationProps,
                    modelId: model.id,
                })
                rows.push(
                    <TableRowLevelStyled
                        key={`model_${modelId}`}
                        size={rowFitSize}
                        sectionLevel={1}
                        onClick={() => toggleModel(model.id)}
                    >
                        {
                            columns.map((column) => (
                                <TableCell
                                    key={column.field}
                                    cellWidth={column.cellWidth}
                                    cellMinWidth={column.cellMinWidth}
                                    cellMaxWidth={column.cellMaxWidth}
                                >
                                    <TableCellContentWrapper>
                                        <TableCellContent>
                                            {
                                                column.field === "title"
                                                    ? <ModelTitle>{`${lang.model}: ${model.name}`}</ModelTitle>
                                                    : column.formatter(modelAvg)
                                            }
                                        </TableCellContent>
                                    </TableCellContentWrapper>
                                </TableCell>
                            ))
                        }
                    </TableRowLevelStyled>,
                )

                if (expandedModelIds.includes(model.id)) {
                    // order by "createdAt" DESC - show the newest on top
                    const key = `model_${model.id}_safetyFunction_null_option_null`
                    const datapoints = datapointList[key] && datapointList[key].datapoints
                        ? datapointList[key].datapoints.slice().sort((a, b) => sort(a, b, "createdAt", DESC))
                        : []

                    rows.push(
                        <DatapointRow key={`model_${model.id}_datapoints`} size={rowFitSize}>
                            <TableCell colSpan={100}>
                                <TableCellContentWrapper style={datapointWrapperStyle}>
                                    {
                                        datapoints.length === 0
                                        && <NoDatapoints />
                                    }
                                    {
                                        datapoints.map((datapoint) => {
                                            const datapointAvg = CalculateQidResultData({
                                                ...baseCalculationProps,
                                                modelId: model.id,
                                                datapointId: datapoint.id,
                                            })
                                            return (
                                                <Datapoint
                                                    key={datapoint.id}
                                                    datapoint={datapoint}
                                                    datapointAvg={datapointAvg}
                                                />
                                            )
                                        })
                                    }
                                </TableCellContentWrapper>
                            </TableCell>
                        </DatapointRow>,
                    )
                }
            }

            model.safetyFunctions.forEach((safetyFunction) => {
                if (!activeSafetyFunction || (activeSafetyFunction && activeSafetyFunction.id === safetyFunction.id)) {
                    if (!activeOption) {
                        const safetyFunctionAvg = CalculateQidResultData({
                            ...baseCalculationProps,
                            modelId: model.id,
                            safetyFunctionId: safetyFunction.id,
                        })
                        rows.push(
                            <TableRowLevelStyled
                                key={`model_${model.id}_safetyFunction_${safetyFunction.id}`}
                                size={rowFitSize}
                                sectionLevel={2}
                                onClick={() => toggleSafetyFunction(safetyFunction.id)}
                            >
                                {
                                    columns.map((column) => (
                                        <TableCell
                                            key={column.field}
                                            cellWidth={column.cellWidth}
                                            cellMinWidth={column.cellMinWidth}
                                            cellMaxWidth={column.cellMaxWidth}
                                        >
                                            <TableCellContentWrapper>
                                                <TableCellContent>
                                                    {
                                                        column.field === "title"
                                                            ? safetyFunction.name
                                                            : column.formatter(safetyFunctionAvg)
                                                    }
                                                </TableCellContent>
                                            </TableCellContentWrapper>
                                        </TableCell>
                                    ))
                                }
                            </TableRowLevelStyled>,
                        )

                        if (expandedSafetyFunctionIds.includes(safetyFunction.id)) {
                            // order by "createdAt" DESC - show the newest on top
                            const key = `model_${model.id}_safetyFunction_${safetyFunction.id}_option_null`
                            const datapoints = datapointList[key] && datapointList[key].datapoints
                                ? datapointList[key].datapoints.slice().sort((a, b) => sort(a, b, "createdAt", DESC))
                                : []

                            rows.push(
                                <DatapointRow key={`safetyFunction_${safetyFunction.id}_datapoints`} size={rowFitSize}>
                                    <TableCell colSpan={100}>
                                        <TableCellContentWrapper style={datapointWrapperStyle}>
                                            {
                                                datapoints.length === 0
                                                && <NoDatapoints />
                                            }
                                            {
                                                datapoints.map((datapoint) => {
                                                    const datapointAvg = CalculateQidResultData({
                                                        ...baseCalculationProps,
                                                        modelId: model.id,
                                                        safetyFunctionId: safetyFunction.id,
                                                        datapointId: datapoint.id,
                                                    })
                                                    return (
                                                        <Datapoint
                                                            key={datapoint.id}
                                                            datapoint={datapoint}
                                                            datapointAvg={datapointAvg}
                                                        />
                                                    )
                                                })
                                            }
                                        </TableCellContentWrapper>
                                    </TableCell>
                                </DatapointRow>,
                            )
                        }
                    }

                    safetyFunction.options.forEach((option) => {
                        if (!activeOption || (activeOption && activeOption.id === option.id)) {
                            const optionAvg = CalculateQidResultData({
                                ...baseCalculationProps,
                                modelId: model.id,
                                safetyFunctionId: safetyFunction.id,
                                optionId: option.id,
                            })
                            rows.push(
                                <TableRow
                                    key={`model_${model.id}_safetyFunction_${safetyFunction.id}_option_${option.id}`}
                                    size={rowFitSize}
                                    onClick={() => toggleOption(option.id)}
                                >
                                    {
                                        columns.map((column) => (
                                            <TableCell
                                                key={column.field}
                                                cellWidth={column.cellWidth}
                                                cellMinWidth={column.cellMinWidth}
                                                cellMaxWidth={column.cellMaxWidth}
                                            >
                                                <TableCellContentWrapper>
                                                    <TableCellContent>
                                                        {
                                                            column.field === "title"
                                                                ? option.name
                                                                : column.formatter(optionAvg)
                                                        }
                                                    </TableCellContent>
                                                </TableCellContentWrapper>
                                            </TableCell>
                                        ))
                                    }
                                </TableRow>,
                            )

                            if (expandedOptionIds.includes(option.id)) {
                                // order by "createdAt" DESC - show the newest on top
                                const key = `model_${model.id}_safetyFunction_${safetyFunction.id}_option_${option.id}`
                                const datapoints = datapointList[key] && datapointList[key].datapoints
                                    ? datapointList[key].datapoints.slice().sort((a, b) => sort(a, b, "createdAt", DESC))
                                    : []

                                rows.push(
                                    <DatapointRow key={`option_${option.id}_datapoints`} size={rowFitSize}>
                                        <TableCell colSpan={100}>
                                            <TableCellContentWrapper style={datapointWrapperStyle}>
                                                {
                                                    datapoints.length === 0
                                                    && <NoDatapoints />
                                                }
                                                {
                                                    datapoints.map((datapoint) => {
                                                        const datapointAvg = CalculateQidResultData({
                                                            ...baseCalculationProps,
                                                            modelId: model.id,
                                                            safetyFunctionId: safetyFunction.id,
                                                            optionId: option.id,
                                                            datapointId: datapoint.id,
                                                        })
                                                        return (
                                                            <Datapoint
                                                                key={datapoint.id}
                                                                datapoint={datapoint}
                                                                datapointAvg={datapointAvg}
                                                            />
                                                        )
                                                    })
                                                }
                                            </TableCellContentWrapper>
                                        </TableCell>
                                    </DatapointRow>,
                                )
                            }
                        }
                    })
                }
            })
        }

        if (!activeModel) {
            rows.push(
                <TableRow key={`model_${model.id}_delimiter`} size={rowSmSize}>
                    <TableCell colSpan={100} />
                </TableRow>,
            )
        }
    })

    return rows
}
export default Rows
